import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { flagLabel } from '@/lib/format';

type Tone = 'default' | 'success' | 'warning' | 'danger' | 'muted' | 'moving' | 'stopped';

const TONES: Record<Tone, string> = {
  default: 'border-border bg-muted/60 text-foreground',
  success: 'border-success/30 bg-success/10 text-success',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  danger: 'border-danger/30 bg-danger/10 text-danger',
  muted: 'border-border bg-muted/50 text-muted-foreground',
  moving: 'border-track-moving/30 bg-track-moving/10 text-track-moving',
  stopped: 'border-track-stopped/30 bg-track-stopped/10 text-track-stopped',
};

export function Badge({
  tone = 'default',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-medium',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * A server flag, rendered as a sentence.
 *
 * The API deliberately emits codes (`not_capturing`) rather than prose, so the
 * wire format survives a copy rewrite. The translation belongs here, once, and
 * an unknown code degrades to its own de-underscored text rather than being
 * dropped — a flag nobody has styled yet is still a flag somebody should see.
 */
export function FlagBadge({ code }: { code: string }) {
  const { label, tone } = flagLabel(code);
  return <Badge tone={tone}>{label}</Badge>;
}

/**
 * A coloured dot, optionally pulsing.
 *
 * Pulsing is reserved for "this is live and moving" — if everything pulses,
 * nothing does.
 */
export function Dot({
  tone,
  pulse = false,
  className,
}: {
  tone: 'success' | 'warning' | 'danger' | 'muted' | 'moving' | 'stopped';
  pulse?: boolean;
  className?: string;
}) {
  const color = {
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
    muted: 'text-muted-foreground',
    moving: 'text-track-moving',
    stopped: 'text-track-stopped',
  }[tone];

  return (
    <span className={cn('relative inline-flex h-2.5 w-2.5 shrink-0', color, className)}>
      {pulse ? <span className="pulse-ring absolute inset-0" aria-hidden /> : null}
      <span className="relative inline-block h-2.5 w-2.5 rounded-full bg-current" />
    </span>
  );
}
